import { FormEvent, ReactElement, useState } from 'react';
import { AlertI, Button } from './Button';
import { InputField } from './InputField';
import './Signup.scss';
import { signup } from '../http';
import { checkValidation, ErrorI } from '../util/functions';
import { PasswordUI } from './PasswordUI';
import Modal from './Modal';

interface SignUpProps {
  setStep: (step: number) => void;
  submitDisable: boolean;
}

export interface SignUpDataI {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword?: string;
}

const initialData: SignUpDataI = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

export default function Signup(props: SignUpProps): ReactElement {
  const [signUpData, setSignupData] = useState<SignUpDataI>(initialData);
  const [termsConditionCheck, setTermsConditionCheck] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [error, setError] = useState<ErrorI>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertI | null>(null);
  const [open, setOpen] = useState(false);

  const handleSubmit = async (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    let failed = false;
    //check for empty field
    let e = checkValidation(signUpData);
    if (passwordStrength < 3) {
      failed = true;
      e = { ...e, password: 'Password is not strong' };
    }

    if (!termsConditionCheck) {
      e = { ...e, termsCondition: 'Terms and conditions is required' };
    }
    setError(e);
    //checking if any other error has occurred
    Object.keys(e).map((key) => {
      if (error[key] !== null) {
        failed = true;
      }
      return key;
    });

    if (failed) {
      return;
    } else {
      //make api call
      setLoading(true);

      await signup(signUpData)
        .then((res) => {
          setAlert({
            type: 'success',
            message:
              'If a matching account was found an email will sent to email address to allow you to reset your password.Please check spam if not available in your inbox',
          });
          setTimeout(() => {
            setLoading(false);
            localStorage.setItem('step', '2');
            localStorage.setItem('email', signUpData.email);
            props.setStep(2);
          }, 2000);
        })
        .catch(function (e) {
          if (e?.response?.data) {
            const error = e?.response?.data;
            setAlert({
              type: 'error',
              message: error.message,
            });
          } else {
            setAlert({
              type: 'error',
              message: 'NetWork error',
            });
          }
          setLoading(false);
        });
    }
  };

  return (
    <form className="form-container" onSubmit={handleSubmit}>
      <div className="auth-form-group">
        <InputField
          value={signUpData?.firstName}
          onChange={(e) => {
            setSignupData({ ...signUpData, firstName: e.target.value });
            if (e.target.value.length) {
              setError({ ...error, firstName: null });
            } else {
              setError({ ...error, firstName: 'First Name is required' });
            }
          }}
          type="text"
          placeholder="Enter your First Name"
        />
        <span className="error">{error?.firstName ? error?.firstName : null}</span>
      </div>
      <div className="auth-form-group">
        <InputField
          value={signUpData?.lastName}
          onChange={(e) => {
            setSignupData({ ...signUpData, lastName: e.target.value });
            if (e.target.value.length) {
              setError({ ...error, lastName: null });
            } else {
              setError({ ...error, lastName: 'Last Name is required' });
            }
          }}
          type="text"
          placeholder="Enter your Last Name"
        />
        <span className="error">{error?.lastName ? error?.lastName : null}</span>
      </div>
      <div className="auth-form-group">
        <InputField
          value={signUpData?.email}
          onChange={(e) => {
            setSignupData({ ...signUpData, email: e.target.value });
            if (e.target.value.length) {
              setError({ ...error, email: null });
            } else {
              setError({ ...error, email: 'Valid email is required' });
            }
          }}
          type="email"
          placeholder="Enter your email"
        />
        <span className="error">{error?.email ? error?.email : null}</span>
      </div>
      <div className="auth-form-group">
        <PasswordUI
          error={error}
          data={signUpData}
          onPasswordChange={(password, strength, pointsCheckFail) => {
            setPasswordStrength(strength);
            setSignupData({ ...signUpData, password: password });
            if (password.length) {
              if (strength < 3 && pointsCheckFail === false) {
                setError({ ...error, password: 'Password is not strong' });
              } else {
                setError({ ...error, password: null });
              }
            } else {
              setError({ ...error, password: 'Password  is required' });
            }
          }}
          onConfirmPasswordChange={(password) => {
            setSignupData({ ...signUpData, confirmPassword: password });
            if (signUpData.password.toLocaleLowerCase() !== password.toLocaleLowerCase()) {
              setError({ ...error, confirmPassword: "Confirm password doesn't matched" });
            } else {
              setError({ ...error, confirmPassword: null });
            }
          }}
        />
      </div>

      <div className="auth-form-group">
        <span>
          <input
            className="checkbox"
            type="checkbox"
            onChange={(e) => {
              setTermsConditionCheck(e.target.checked);
              if (e.target.checked) {
                setError({ ...error, termsCondition: null });
              } else {
                setError({ ...error, termsCondition: 'Terms and conditions is required' });
              }
            }}
            name="checkbox"
          />
          <span className="text-black remember-me " style={{ cursor: 'pointer' }}>
            I agree to the{' '}
            <a
              className="text-link"
              href="https://www.biniyog.com.bd/terms-conditions"
              target="_blank"
              rel="noreferrer"
            >
              terms and conditions
            </a>
          </span>
        </span>
        <br />
        <span className="error">{error?.termsCondition ? error?.termsCondition : null}</span>
      </div>

      <Button
        type="submit"
        loading={loading}
        alert={alert}
        disabled={props.submitDisable ? props.submitDisable : loading}
      >
        Register
      </Button>
      <Modal
        visible={open}
        onClose={() => {
          setOpen(false);
        }}
        title="Terms & Conditions"
      >
        <p className="teams">
          <p>
            <strong>IMPORTANT-READ CAREFULLY: </strong>
            <br /> <br />
            Biniyog.com Ltd. ("we", "us","biniyog.com.bd" "biniyog") is committed to protecting theprivacy of the users
            ("you" / "your" /"yourself") of its website/software applications offered by Biniyog(collectively,
            "Applications") and has provided this privacy policy("Policy") to familiarize you with the manner in which
            Biniyog usesand discloses your information collected through the Applications.
            <br /> <br />
            The terms of the Policy provided herein govern youruse of any and all of the Applications and the
            information accessible on orfrom these Applications. The Policy also lays down how Biniyog may collect,
            useand share any information you provide. Biniyog reserves the right, at itsdiscretion, to change, modify,
            add or remove portions of this Policy at anytime. Biniyog recommends that you review this Policy
            periodically to ensurethat you are aware of the current privacy practices.
            <br />
            <br />
            This Policy shall be construed as provided incompliance with ICT Act, 2006
            <br />
            <br />
            <strong>1) REGISTRATION</strong>
            <br />
            <br />
            By registering, you certify that all information you provide, now or in the future, is accurate.Biniyog
            reserves the right, in its sole discretion, to deny you access to this website or any portion thereof
            without notice for the following reasons (a) immediately by Biniyog for any unauthorized access or use by
            you (b) immediately by Biniyog if you assign or transfer (or attempt the same) any rights granted to you
            under this Agreement; (c) immediately, if you violate any of the other terms and conditions of this User
            Agreement
            <br />
            <br />
            <br />‍
          </p>
        </p>
      </Modal>
    </form>
  );
}
